import { Box, Paper, Typography, IconButton, Chip, Tooltip } from '@mui/material';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';
import type { Project } from '@altyor-kronos/shared-types';
import { colors } from '../../core/theme/theme';

export interface ProjectCardProps {
  project: Project;
  clientName?: string;
  favourited: boolean;
  hidden: boolean;
  onToggleFavourite: (projectId: string) => void;
  onToggleHidden: (projectId: string) => void;
}

/**
 * Project card — the atomic tile rendered in Mes/Tous grids on the Projects Hub.
 *
 * Design contract (docs/design/mui-design-guide.md):
 * - `borderRadius: 0`, `elevation={0}`, 1px light-gray border
 * - Colour accent bar along the top (uses lightGray accent)
 * - Square avatars / chips-with-6px-radius are handled by global theme
 */
export default function ProjectCard({
  project,
  clientName,
  favourited,
  hidden,
  onToggleFavourite,
  onToggleHidden,
}: ProjectCardProps) {
  const { t } = useTranslation();
  const accent = colors.lightGray;

  return (
    <Paper
      variant="outlined"
      elevation={0}
      sx={{
        position: 'relative',
        p: 2,
        borderColor: colors.lightGray,
        borderRadius: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        opacity: hidden ? 0.55 : 1,
        transition: 'opacity 120ms ease',
        '&:hover': { backgroundColor: colors.peach },
      }}
      data-testid={`project-card-${project.id}`}
    >
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, backgroundColor: accent }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mt: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.5 }}>
            {project.code}
          </Typography>
          <Typography
            variant="h4"
            component={RouterLink}
            to={`/projects/${project.id}`}
            sx={{
              display: 'block',
              color: 'inherit',
              textDecoration: 'none',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {project.name}
          </Typography>
          {clientName && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {clientName}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
          <Tooltip title={favourited ? t('projects.hub.unfavourite') : t('projects.hub.favourite')}>
            <IconButton
              size="small"
              aria-label={favourited ? t('projects.hub.unfavourite') : t('projects.hub.favourite')}
              // `favourited` rather than a static value: the E2E instant-refresh
              // guard reads it to assert the star flipped, without depending on
              // the active locale's aria-label.
              data-testid={`project-favourite-${favourited ? 'on' : 'off'}-${project.id}`}
              onClick={() => onToggleFavourite(project.id)}
            >
              {favourited ? (
                <StarIcon fontSize="small" sx={{ color: colors.yellow, stroke: colors.textMuted, strokeWidth: 1 }} />
              ) : (
                <StarBorderIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title={hidden ? t('projects.hub.unhide') : t('projects.hub.hide')}>
            <IconButton
              size="small"
              aria-label={hidden ? t('projects.hub.unhide') : t('projects.hub.hide')}
              data-testid={`project-hidden-${hidden ? 'on' : 'off'}-${project.id}`}
              onClick={() => onToggleHidden(project.id)}
            >
              {hidden ? (
                <VisibilityOffOutlinedIcon fontSize="small" />
              ) : (
                <VisibilityOutlinedIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 'auto' }}>
        <Chip size="small" variant="outlined" label={project.currentProgram} sx={{ borderRadius: '6px' }} />
      </Box>
    </Paper>
  );
}
